import {
    Alert,
    AlertNote,
    AlertsApiResponse,
    AlertStatusEntry,
} from './interfaces'

export type AlertTimelineEntry = {
    id: string
    userEmail: string
    createdAt: string
    noteText: string
    isStatus: boolean
    status: number | null
}

/**
 * An alert's `id` field is a rule/condition dedup key, not a unique-per-row
 * identifier - the same `id` can legitimately repeat across different alert
 * rows (different rules, or the same rule firing again later). The only
 * reliable way to identify one specific alert row is this full composite key.
 *
 * UBQ-5164: matching on `id` alone caused marking one alert as read to also
 * flip a different alert that happened to share the same `id`.
 */
export const matchesAlert = (a: Alert, target: Alert): boolean =>
    a.entityId === target.entityId &&
    a.timestamp === target.timestamp &&
    a.id === target.id &&
    a.ruleId === target.ruleId

/**
 * Same composite-uniqueness rationale as `matchesAlert`: `alert.id` alone
 * repeats across distinct rows, so it can't key per-row UI state (e.g. which
 * alert's note editor is open) either. Use this wherever a single alert row
 * needs a stable, unique identity.
 */
export const getAlertKey = (alert: Alert): string =>
    `${alert.entityId}_${alert.timestamp}_${alert.id}_${alert.ruleId}`

/**
 * Swaps a just-created note's client-generated `temp-<timestamp>` id for the
 * server-assigned UUID once the create request resolves. Without this, the
 * temp id stays in the cache indefinitely (there is no success-path refetch)
 * and gets sent straight back to PUT /alerts/note on the next edit, which the
 * backend rejects because `noteId` is typed as a UUID.
 */
export const reconcileSavedNote = (
    pages: AlertsApiResponse[],
    alert: Alert,
    tempNoteId: string,
    savedNote: AlertNote,
): AlertsApiResponse[] =>
    pages.map((page) => ({
        ...page,
        alerts: page.alerts.map((a) => {
            if (!matchesAlert(a, alert)) return a
            return {
                ...a,
                notes: (a.notes || []).map((n) =>
                    n.id === tempNoteId ? savedNote : n,
                ),
            }
        }),
    }))

/**
 * Merges an alert's notes and status-change history into a single
 * chronological timeline (newest first) for the Acknowledgment Status panel.
 */
export const buildAlertTimeline = (
    notes: AlertNote[] | undefined,
    statusHistory: AlertStatusEntry[] | undefined,
): AlertTimelineEntry[] => {
    const combined = [...(notes ?? []), ...(statusHistory ?? [])] as (
        | AlertNote
        | AlertStatusEntry
    )[]

    return combined
        .map((item) => ({
            id: item.id,
            userEmail: item.userEmail,
            createdAt: item.createdAt,
            noteText: 'noteText' in item ? item.noteText : '',
            isStatus: 'status' in item,
            status: 'status' in item ? item.status : null,
        }))
        .sort(
            (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime(),
        )
}
